/**
 * Shared job-ID classifications.
 *
 * Keeping these in one place so the render-routing logic (Character.vue) and
 * the UI-gating logic (OutfitCheckbox.vue, randomCharacter.js) don't drift.
 */

// Jobs the PRIMARY render backend (visualrag-api.ragnarok.wiki) can't render —
// it returns a 364-byte placeholder. These are the standard 4th classes, which
// we route to the iRO Wiki backend instead. (Star Emperor 4239 / Soul Reaper
// 4240 are NOT here — the primary backend renders them fine.)
export const FALLBACK_RENDER_JOB_IDS = new Set([
  // Standard 4th classes:
  4252, 4253, 4254, 4255, 4256, 4257, 4258, 4259, 4260, 4261, 4262, 4263, 4264,
  // Expanded 4th classes (primary lacks their sprites; iRO Wiki renders them):
  4302, 4303, 4304, 4305, 4306, 4307, 4308,
]);

// Item view-IDs the PRIMARY backend can't render yet (newer costume items it
// hasn't added). If an equipped headgear/garment uses one of these, route the
// whole render to the iRO Wiki backend, which has them. Grow as new items are
// reported. (Verified: primary returns the base sprite, iro renders the item.)
export const FALLBACK_RENDER_VIEW_IDS = new Set([
  2707, // Peruca da Via Láctea / Costume Twin Milkyway (item 420525, Lower)
  // New-update items the primary backend lacks but the iRO Wiki backend renders:
  207,  // Espada Cibernética (480419, garment)
  213,  // Cajado de Bougainvillea (480430, garment)
  214,  // Sombrinha da Sinhazinha (480431, garment)
  254,  // Manto do Dragão Guerreiro (480517, garment)
  2077, // Boneca de Elly (400128, top)
  2124, // Peruca Trançada de Coelho (420067, Lower)
  // 2026-07-07 update batch (primary lacks all of these; iRO Wiki renders them):
  258,  // Asas Caídas de Freya (480541, garment) — shares sprite w/ 20189
  2360, // Cabelos de Freya (400427, Lower)
  2668, // Consecrate Fides Vermelha (400789, top)
  2708, // Piscadela Cósmica (410448, mid)
  2396, // Consecrate Fides (420222, Lower)
  2719, // Cabelo de Miriam (420546, Lower)
  223,  // Mochila de Vinha (480456, garment)
  203,  // Asas de Letícia (480407, garment) — shares sprite w/ 440004
  2115, // Cabelos de Betelgeuse (400148, Lower)
  2114, // Boneco de Betelgeuse (400147, top)
  // Latest update batch — iRO Wiki confirmed to render these (primary was down
  // during routing, so these route to iRO to guarantee they render):
  2543, // Boina de Marin (400739, top)
  1051, // Leque de Veraneio (31304, bot)
  2138, // Festa Macarrônica (400160, top)
  // 2026-08-18 update (iRO Wiki renders these; primary is still 502):
  383,  // Nuvens Auspiciosas (420351, bot)
  2683, // Peruca Longa de Pétalas (420500, bot)
  2528, // Piscadela de Freya (410320, mid+bot)
  // 2026-09-15 update. (Parfait Gigante de Pitaya, garment v257, is deliberately
  // NOT here: this set is checked against headgear AND garment viewIDs, and
  // headgear 257 is Gálea da Morrigane, which would then be force-routed too.)
  2450, // Cetros do Mau-Olhado (410286, mid)
  // 2026-10-01 update. Only the brand-new sprites are listed; Coroa de Diabolus
  // (v364) and Máscara do Sorriso (v65) reuse old sprites the primary has, and
  // Espada de Himmelmez is a garment (v218); see the namespace note on 2450's
  // batch above.
  2578, // Fantasma de Gatinho (420367, bot)
  2530, // Cachecol de Ossos (420336, bot)
]);

// 4th-class jobs that have NO alternate outfit ("Roupa Alternativa"). Both render
// backends silently ignore the `outfit` flag for these (verified: identical
// sprite hash with outfit 0 vs 1), so toggling it is a confusing no-op. We
// disable the toggle when one of these jobs is selected.
//
// Covers: Star Emperor (4239), Soul Reaper (4240), the standard 4th classes
// (4252-4264), and the expanded 4th branch (4302-4308, not yet on LatamRO but
// listed so the rule already applies if/when they're enabled).
export const NO_ALT_OUTFIT_JOB_IDS = new Set([
  4239, 4240,
  4252, 4253, 4254, 4255, 4256, 4257, 4258, 4259, 4260, 4261, 4262, 4263, 4264,
  4302, 4303, 4304, 4305, 4306, 4307, 4308,
]);
